import React from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPartnersData } from '../data/partnersData';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, var(--primary) 0%, #1a5490 100%);
  padding: 60px 20px;
  margin-bottom: 60px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse"><path d="M 100 0 L 0 0 0 100" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="1"/></pattern></defs><rect width="100%" height="100%" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 40px 20px;
    margin-bottom: 40px;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 48px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 32px;
  }
`;

const LogoWrapper = styled.div`
  flex: 0 0 280px;
  background: #fff;
  border-radius: 20px;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }

  img {
    max-width: 100%;
    max-height: 180px;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    flex: 0 0 auto;
    width: 100%;
    max-width: 280px;
    padding: 32px;
  }
`;

const HeroInfo = styled.div`
  flex: 1;
  color: #fff;
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin: 0;
  color: #fff;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    font-size: 32px;
    text-align: center;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  margin: 16px 0 0;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.6;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    font-size: 16px;
    text-align: center;
  }
`;

const ContentWrapper = styled.div`
  padding: 0 20px 60px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 0 20px 40px;
  }
`;

const Content = styled.div`
  background: var(--card);
  border: 1px solid var(--bdr);
  border-radius: 24px;
  padding: 48px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  line-height: 1.8;
  white-space: pre-wrap;
  font-size: 16px;
  color: var(--fg);

  @media (max-width: 768px) {
    padding: 28px;
    font-size: 15px;
  }

  h2 {
    font-size: 28px;
    font-weight: 700;
    margin: 40px 0 20px;
    color: var(--primary);
    padding-bottom: 12px;
    border-bottom: 2px solid var(--primary);

    &:first-child {
      margin-top: 0;
    }
  }

  h3 {
    font-size: 22px;
    font-weight: 600;
    margin: 32px 0 16px;
    color: var(--fg);
  }

  p {
    margin: 0 0 20px;
    color: var(--fg);
  }

  ul, ol {
    margin: 0 0 20px;
    padding-left: 28px;
    color: var(--fg);
  }

  li {
    margin-bottom: 12px;
    line-height: 1.7;
  }

  strong {
    font-weight: 600;
    color: var(--primary);
  }

  a {
    color: var(--primary);
    text-decoration: underline;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
      text-decoration: none;
    }
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);

  @media (max-width: 768px) {
    margin: 20px auto 0;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 18px;
  color: var(--fg);
  opacity: 0.7;
`;

export default function Partner() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [partner, setPartner] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // Получаем данные партнёров на текущем языке
    const partnersData = getPartnersData(i18n.language || 'ru');
    const partnersMap = {};
    partnersData.forEach(p => {
      partnersMap[p.slug] = p;
    });

    setTimeout(() => {
      const foundPartner = partnersMap[slug];
      setPartner(foundPartner || null);
      setLoading(false);
    }, 300);
  }, [slug, i18n.language]);

  React.useEffect(() => {
    if (partner) {
      document.title = `${partner.name} | АСТОРИУС`;
    }
  }, [partner]);

  if (loading) {
    return (
      <Container>
        <LoadingMessage>{t('common.loading', 'Загрузка...')}</LoadingMessage>
      </Container>
    );
  }

  if (!partner) {
    return (
      <Container>
        <LoadingMessage>{t('partner.notFound', 'Партнёр не найден')}</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Hero>
        <HeroContent>
          <LogoWrapper>
            <img 
              src={partner.logo} 
              alt={partner.name} 
            />
          </LogoWrapper>
          <HeroInfo>
            <Title>{partner.name}</Title>
            <Subtitle>{t('partner.subtitle', 'Партнёр компании АСТОРИУС')}</Subtitle>
            <Badge>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 0L10.163 5.837L16 8L10.163 10.163L8 16L5.837 10.163L0 8L5.837 5.837L8 0Z" fill="currentColor"/>
              </svg>
              {t('partner.verified', 'Проверенный партнёр')}
            </Badge>
          </HeroInfo>
        </HeroContent>
      </Hero>

      <ContentWrapper>
        <Content>
          {partner.description || partner.short_description}
        </Content>
      </ContentWrapper>
    </Container>
  );
}
